import type { HotkeyIndicatorCharacter } from "@replo/design-system/utils/hotkeys";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { IconType } from "react-icons";

type TextCommand =
  | "toggleH1Text"
  | "toggleH2Text"
  | "toggleH3Text"
  | "toggleH4Text"
  | "toggleH5Text"
  | "toggleH6Text"
  | "toggleBoldText"
  | "toggleLinkText"
  | "toggleItalicText"
  | "toggleUnderlineText"
  | "toggleStrikethroughText"
  | "toggleBuildAssistantMinimized"
  | "toggleBulletList"
  | "toggleNumberedList"
  | "decreaseFontSize"
  | "increaseFontSize"
  | "decreaseLetterSpacing"
  | "increaseLetterSpacing"
  | "decreaseLineHeight"
  | "increaseLineHeight"
  | "decreaseFontWeight"
  | "increaseFontWeight";

type EditCommand =
  | "undo"
  | "redo"
  | "copy"
  | "copyStyles"
  | "paste"
  | "pasteStyles"
  | "delete"
  | "duplicate"
  | "groupIntoContainer"
  | "saveComponentTemplate"
  | "exportToSection"
  | "setWidthToFillAvailable"
  | "setWidthToWrapContent"
  | "setHeightToFillAvailable"
  | "setHeightToWrapContent"
  | "toggleCodeEditor"
  | "alignLeft"
  | "alignRight"
  | "alignTop"
  | "alignBottom"
  | "alignHorizontalCenter"
  | "alignVerticalCenter";

type ViewCommand =
  | "toggleVisibility"
  | "togglePreviewMode"
  | "grabCanvas"
  | "toggleBuildAssistantMinimized"
  | "setDesignPanel"
  | "setConfigPanel"
  | "setInteractionsPanel"
  | "openProjectSettings"
  | "moveUpInTheTree"
  | "moveDownInTheTree"
  | "zoomIn"
  | "zoomOut"
  | "resetZoom";

type ReploGlobalCommand = EditCommand | TextCommand | ViewCommand;

type CommandData = {
  label: string;
  hotkey?: HotkeyIndicatorCharacter[];
  icon?:
    | { type: "iconComponent"; component: IconType | LucideIcon }
    | { type: "create" }
    | { type: "publishedStatus"; isPublished: boolean }
    | { type: "custom"; content: ReactNode };
};

type LeafCommandItem = CommandData & {
  isDisabled?: boolean;
  onSelect: () => void;
};

type GlobalLeafCommandItem = CommandData & {
  isDisabled?: boolean;
  command: ReploGlobalCommand;
};

type ChildrenCommandItem = CommandData & {
  type: "children";
  isDisabled?: boolean;
  children: Record<string, CommandItem>;
};

export type CommandItem =
  | LeafCommandItem
  | GlobalLeafCommandItem
  | ChildrenCommandItem;

export const commandItemHasChildren = (
  commandItem: CommandItem,
): commandItem is CommandItem & {
  type: "children";
  children: Record<string, CommandItem>;
} => "type" in commandItem && commandItem.type === "children";

export const commandItemHasGlobalCommand = (
  commandItem: CommandItem,
): commandItem is GlobalLeafCommandItem => "command" in commandItem;

export type CommandGroup = {
  id: string;
  heading: string;
  items: Record<string, CommandItem>;
};

export type CommandGroupWithActions = CommandGroup & {
  items: Record<string, CommandItem>;
};
